import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useScrollSections } from '@/hooks/useScrollSections';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { smoothScrollToSection } = useScrollSections();
  const navigate = useNavigate();

  // Enhanced scroll to section function that handles both in-page and cross-page navigation
  const scrollToSection = (sectionId: string, path?: string) => {
    // If there's a path, we need to navigate to that page first, then scroll
    if (path) {
      navigate(path, { state: { scrollToId: sectionId } });
      return;
    }
    
    // In-page navigation
    const element = document.getElementById(sectionId);
    if (element) {
      // Use the smooth scroll function from our hook
      smoothScrollToSection(sectionId);
    }
  };

  const footerLinks = {
    "Market Intelligence": [
      { name: "Pricing", href: "#pricing", section: "pricing" },
      { name: "Use Cases", href: "#use-cases", section: "use-cases" },
      { name: "Market Research", path: "/market-research" },
      { name: "Competitive Analysis", path: "/competitive-analysis" }
    ],
    "Company": [
      { name: "Market Insights Blog", path: "/blog" },
      { name: "Contact", path: "/contact" }
    ],
    "Resources": [
      { name: "Vendor Competition Guides", path: "/help" },
      { name: "Supplier Research FAQ", path: "/faq" },
      { name: "Market Reports", path: "/reports" }
    ],
    "Legal": [
      { name: "Privacy Policy", path: "/privacy" },
      { name: "Terms of Service", path: "/terms" },
      { name: "Data Processing", path: "/data-processing" },
      { name: "Cookie Policy", path: "/cookie-policy" }
    ]
  };

  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Logo and company info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-inter font-bold text-3xl text-blue-400">AX</span>
            </div>
            <p className="text-gray-400 text-sm max-w-xs mb-4">
              AI-powered market intelligence that delivers comprehensive insights on vendor competition and supplier analysis at a fraction of the cost of traditional market research.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-gray-400 hover:text-blue-500">
                <span className="sr-only">Twitter</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-500">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
          
          {/* Navigation links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className="lg:col-span-1">
              <h3 className="text-gray-100 font-semibold mb-4 capitalize">{category}</h3>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    {link.path ? (
                      <Link 
                        to={link.path} 
                        className="text-gray-400 hover:text-blue-500 text-sm flex items-center transition-colors duration-300"
                        onClick={() => {
                          if (link.section) {
                            scrollToSection(link.section, link.path);
                          }
                        }}
                      >
                        {link.name}
                        {link.name.includes("API") && (
                          <ArrowUpRight className="ml-1 h-3 w-3" />
                        )}
                      </Link>
                    ) : (
                      <a 
                        href={link.href} 
                        className="text-gray-400 hover:text-blue-500 text-sm flex items-center transition-colors duration-300"
                        onClick={(e) => {
                          if (link.section) {
                            e.preventDefault();
                            scrollToSection(link.section);
                          }
                        }}
                      >
                        {link.name}
                        {link.name.includes("API") && (
                          <ArrowUpRight className="ml-1 h-3 w-3" />
                        )}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} AnalystX. All rights reserved. <span className="hidden md:inline">The ultimate platform for market intelligence and vendor competition analysis.</span>
          </p>
          <div className="mt-4 md:mt-0">
            <select 
              className="bg-gray-800 border border-gray-700 text-gray-300 rounded-md text-sm px-3 py-1"
              defaultValue="en"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
